// Client-side search popularity tracking with time-decayed ranking.
// Schema (localStorage key = STORAGE_KEY):
//   { [normalised_query]: { count: number, lastSearchedAt: number (ms epoch) } }
// Backward compat: legacy entries stored as a plain number are migrated on read
// (treated as lastSearchedAt = time of first read, preserving relative rank).
//
// Decay formula: score = count * 0.5^((now - lastSearchedAt) / HALF_LIFE_MS)
// Half-life = 7 days — a query searched 7 days ago scores half of one searched today.

const STORAGE_KEY = 'gar_search_history';
const MAX_STORED = 100;
const HALF_LIFE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function getStore() {
  return globalThis.localStorage ?? null;
}

/**
 * Compute the time-decayed score for a stored entry.
 * @param {{ count: number, lastSearchedAt: number }} entry
 * @param {number} now - current timestamp (ms)
 * @returns {number}
 */
export function decayedScore(entry, now) {
  const age = Math.max(0, now - entry.lastSearchedAt);
  return entry.count * Math.pow(0.5, age / HALF_LIFE_MS);
}

function load(store = getStore(), now = Date.now()) {
  if (!store) return {};
  let raw;
  try {
    raw = JSON.parse(store.getItem(STORAGE_KEY) ?? '{}');
  } catch {
    return {};
  }
  const result = {};
  for (const [key, val] of Object.entries(raw)) {
    if (typeof val === 'number') {
      // Migrate legacy flat-count schema
      result[key] = { count: val, lastSearchedAt: now };
    } else if (val && typeof val.count === 'number') {
      result[key] = val;
    }
  }
  return result;
}

function save(map, store = getStore()) {
  if (!store) return;
  try {
    const entries = Object.entries(map).sort((a, b) => b[1].count - a[1].count);
    const trimmed = Object.fromEntries(entries.slice(0, MAX_STORED));
    store.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  } catch {
    // Quota exceeded or private browsing — silently ignore.
  }
}

/**
 * Record that the user searched for `query`.
 * Normalises the query to lowercase-trimmed before storing.
 * @param {string} query
 * @param {Storage} [_store] - injectable for tests
 * @param {number} [_now] - current timestamp injectable for tests
 */
export function recordSearch(query, _store = undefined, _now = undefined) {
  const key = (query ?? '').trim().toLowerCase();
  if (!key) return;
  const store = _store ?? getStore();
  const now = _now ?? Date.now();
  const map = load(store, now);
  const prev = map[key];
  map[key] = {
    count: (prev?.count ?? 0) + 1,
    lastSearchedAt: now,
  };
  save(map, store);
}

/**
 * Return the top N searches ordered by time-decayed score (descending).
 * Recent searches outrank older searches with the same raw count.
 * @param {number} [n=8]
 * @param {Storage} [_store] - injectable for tests
 * @param {number} [_now] - current timestamp injectable for tests
 * @returns {Array<{query: string, count: number, score: number}>}
 */
export function getTopSearches(n = 8, _store = undefined, _now = undefined) {
  const store = _store ?? getStore();
  const now = _now ?? Date.now();
  const map = load(store, now);
  return Object.entries(map)
    .map(([query, entry]) => ({ query, count: entry.count, score: decayedScore(entry, now) }))
    .sort((a, b) => b.score - a.score)
    .slice(0, n);
}

/**
 * Clear all stored search history.
 * @param {Storage} [_store] - injectable for tests
 */
export function clearSearchHistory(_store = undefined) {
  const store = _store ?? getStore();
  if (!store) return;
  try {
    store.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
